import React, { useCallback, useState } from 'react';
import { useTranslations } from 'js/hooks/i18n';
import { Button } from 'js/components/common/Button';
import style from './ChangeLimit.scss';
import * as helpers from '../../../Limits/helpers';
import { DAY, WEEK, CURRENT_LIMITS, MIN_INDEX } from './constants';
import { useDeviceSelector } from 'js/store/hooks';
import { pick } from 'ramda';
import { useFormatter } from 'js/hooks/useFormatter';
import { saveLimits } from 'js/components/main/ResponsibleGaming/server';
import { sendDepositLimits } from '../../DepositLimits/actions';
import {
    DEPOSIT_UNLIMITED,
    DEPOSIT_PERIODS,
    RESPONSIBLE_GAMING_DEPOSIT_LIMITS,
    LOSS_UNLIMITED_STR,
    LOSS_UNLIMITED,
    UNLIMITED_STR
} from '../../constants';
import { dispatcher } from 'dispatcher';

export const ChangeLimit = ({
                                onClose,
                                portletType,
                                initialValues,
                                availablePeriods,
                                actualLimits,
                                valuesLimit,
                                limitAction,
                                fetchPortlet,
                                reFetchPicker
}) => {

    const dailyIndex = actualLimits.day?.limit?.amount ? valuesLimit.indexOf(Number(actualLimits.day?.limit?.amount)) : 0;
    const weeklyIndex = actualLimits.week?.limit?.amount ? valuesLimit.indexOf(Number(actualLimits.week?.limit?.amount)) : 0;
    const monthlyIndex = actualLimits.month?.limit?.amount ? valuesLimit.indexOf(Number(actualLimits.month?.limit?.amount)) : 0;
    const indexArray = [dailyIndex, weeklyIndex, monthlyIndex];
    const [day, setDailyLimitValue] = useState(dailyIndex);
    const [week, setWeeklyLimitValue] = useState(weeklyIndex);
    const [month, setMonthlyLimitValue] = useState(monthlyIndex);
    const [defaultLimit, setDefaultLimits] = useState(true);
    const [waitingRequest, setWaitingRequest] = useState(false);
    const [isAnimated, setAnimation] = useState(false);
    const formatter = useFormatter();
    const tPrefix = `playtech.mobile.${portletType}.`;
    const t = useTranslations(tPrefix);
    const { os, browserName } = useDeviceSelector(pick(['os', 'browserName']));
    const isAndroid = os === 'android';
    const isSafariDesktop = os === 'macintosh' && browserName === 'safari';
    const maxIndex = valuesLimit.length - 1;
    const { currency } = window.Playtech.fetchUserDetails();
    const DELAY = 1000;
    const isDepositLimit = portletType === RESPONSIBLE_GAMING_DEPOSIT_LIMITS;

    const checkDefaultValues = () => {
        const values = [];
        document.querySelectorAll('input[type=range]').forEach(el => {
            values.push(Number(el.value));
        });

        if (JSON.stringify(values) === JSON.stringify(indexArray)) {
            setDefaultLimits(true);
        } else {
            setDefaultLimits(false);
        }
    };

    const showAmountLimit = (period) => {
        const currencyCode = actualLimits[period]?.limit ? actualLimits[period]?.limit?.currencyCode : currency;
        const result = getValue(period);

        if (result === DEPOSIT_UNLIMITED) {
            return t('noLimit');
        }

        if (result === LOSS_UNLIMITED) {
            return t('noLimit');
        }

        return String(result).length > 4 ?
            formatter.formatCurrency(result, { currencyCode, symbolAtTheLeft: true, precision: 0, thSeparator: ' ' }) :
            formatter.formatCurrency(result, { currencyCode, symbolAtTheLeft: true, precision: 0, thSeparator: '' });
    };

    const getPeriod = (period) => period === DAY ? day : period === WEEK ? week : month;

    const changeDailyLimit = (event, period) => {
        setDailyLimitValue(event.target.value);
        getValue(period);

        if (Number(event.target.value) > Number(week)) {
            changeWeeklyLimit(event, period);
        }

        if (Number(event.target.value) > Number(month)) {
            changeMonthlyLimit(event, period);
        }
    };

    const changeWeeklyLimit = (event, period) => {
        setWeeklyLimitValue(event.target.value);
        getValue(period);

        if (Number(event.target.value) < Number(day)) {
            changeDailyLimit(event, period);
        }

        if (Number(month) < Number(event.target.value)) {
            changeMonthlyLimit(event, period);
        }
    };

    const changeMonthlyLimit = (event, period) => {
        setMonthlyLimitValue(event.target.value);
        getValue(period);

        if (Number(event.target.value) < Number(day)) {
            changeDailyLimit(event, period);
        }

        if (Number(event.target.value) < Number(week)) {
            changeWeeklyLimit(event, period);
        }
    };

    const changeRange = (event, period) => {
        if (waitingRequest) {
            return;
        }

        if (period === DAY) {
            changeDailyLimit(event, period);
        } else if (period === WEEK) {
            changeWeeklyLimit(event, period);
        } else {
            changeMonthlyLimit(event, period);
        }
    };

    const getValue = (period) => {
        if (period === DAY) {
            return valuesLimit[day];
        } else if (period === WEEK) {
            return valuesLimit[week];
        }
            return valuesLimit[month];

    };

    const onTouchStart = e => {
        if (isAndroid) {
            e.target.classList.add('pressed');
        }
    };

    const onMouseLeave = () => {
        if (isSafariDesktop) {
            checkDefaultValues();
        }
    };

    const onTouchEnd = e => {
        if (isAndroid) {
            e.target.classList.remove('pressed');
        }

        checkDefaultValues();
    };

    const changeLimit = () => isDepositLimit ? changeDepositLimit() : changeLossLimit();

    const changeLossLimit = useCallback(async () => {
        setAnimation(true);
        setWaitingRequest(true);
        const limitValues = [];
        const updatedInitialValues = Object.assign({}, initialValues);

        document.querySelectorAll('input[type=range]').forEach(el => {

            if (valuesLimit[el.value] === Number(LOSS_UNLIMITED)) {
                limitValues.push(LOSS_UNLIMITED_STR);
            } else {
                limitValues.push(String(valuesLimit[el.value]));
            }
        });

        CURRENT_LIMITS.forEach((el, ind) => {
            updatedInitialValues[el] = limitValues[ind];
        });

        const { limits } = helpers.prepareLimitsForSave(initialValues, updatedInitialValues, availablePeriods);

        try {
            await saveLimits({ type: portletType, limits, action: limitAction });
            await fetchPortlet({ type: portletType }, { showLoader: false });

            setTimeout(() => {
                setAnimation(false);
                reFetchPicker(true);
                setTimeout(() => {
                    onClose();
                }, DELAY);
            }, DELAY);

        } catch (error) {
            console.error(error);
            setAnimation(false);
            setWaitingRequest(false);
        }

    }, [fetchPortlet, saveLimits, reFetchPicker]);

    const changeDepositLimit = useCallback(async () => {
        setAnimation(true);
        setWaitingRequest(true);

        const limitValues = [];
        const updatedInitialValues = {};
        const limits = [];
        const timePeriods = Object.keys(actualLimits);
        const updatedLimitArray = [];

        document.querySelectorAll('input[type=range]').forEach(el => {
            if (valuesLimit[el.value] === DEPOSIT_UNLIMITED) {
                limitValues.push(UNLIMITED_STR);
            } else {
                limitValues.push(String(valuesLimit[el.value]));
            }
        });

        timePeriods.forEach(el => {
            updatedLimitArray.push(DEPOSIT_PERIODS[availablePeriods.indexOf(el)]);
        });

        updatedLimitArray.forEach((el, ind) => {
            updatedInitialValues[el] = limitValues[ind];
        });

        for (const key in updatedInitialValues) {
            limits.push({
                amount: updatedInitialValues[key],
                timePeriod: key
            });
        }

        try {
            await sendDepositLimits(limits);
            await fetchPortlet({ type: portletType }, { showLoader: false });

            setTimeout(() => {
                setAnimation(false);
                reFetchPicker(true);
                dispatcher.dispatch('quickDeposit:updated');
                setTimeout(() => {
                    onClose();
                }, DELAY);
            }, DELAY);

        } catch (error) {
            console.error(error);
            setAnimation(false);
            setWaitingRequest(false);
        }

    }, [fetchPortlet, sendDepositLimits, reFetchPicker]);

    return <div className={'cms-change-limit'}>
        {availablePeriods.length && availablePeriods.map((period, index) => (
            <div key={index}
                 className={style.changeLimitPeriod}
                 data-limit={period}>
                <div className={style.timePeriod}>
                    <div className={style.titleTimeLimit}>{isDepositLimit ?
                        t(`active.${DEPOSIT_PERIODS[index]}`) :
                        t(`active.${period}`)
                    }</div>
                    <div className={style.limitsAmountContainer}>
                        <div className={style.valueLimit}>{`${showAmountLimit(period)}`}</div>
                    </div>
                </div>
                <input
                    type='range'
                    onChange={(e) => changeRange(e, period)}
                    min={MIN_INDEX}
                    max={maxIndex}
                    step={1}
                    onTouchStart={e => onTouchStart(e)}
                    onTouchEnd={e => onTouchEnd(e)}
                    className={waitingRequest ? style.disableRange : style.range}
                    value={`${getPeriod(period)}`}
                    onClick={checkDefaultValues}
                    onMouseLeave={onMouseLeave}
                    list='tickmarks'
                >
                </input>

                <div className={style.rangeContainer}>
                    <datalist className={style.dots} id='tickmarks'>
                        {valuesLimit.map((values) => (
                            <span key={values}
                                    className={style.tick}>
                            </span>
                        ))}
                    </datalist>
                </div>

            </div>
        ))}
            <div className={style.formActions}>
                {isAnimated ? (
                    <Button
                        type="button"
                        className={style.animatedBtn}
                    >
                        <div className={style.animation}></div>
                    </Button>
                ) : (
                    <Button
                        type="button"
                        onClick={changeLimit}
                        className={defaultLimit ? style.disabledBtn : waitingRequest ? style.successBtn : style.btn}
                    >
                        <span>{waitingRequest ? t('success') : t('applyDecreaseLimit')}</span>
                    </Button>
                )}
            </div>
    </div>;
};
